import { useEffect, useMemo } from 'react';
import { createShallowStore, createStore } from '@frontend/store';
import { sentry } from '@frontend/tracking';
import {
  useGetSelectedCalendarDateAppointmentInfo,
  useGetSelectedCalendarDateAppointmentList,
} from '../hooks/use-get-selected-calendar-date-appointment-info';
import { ExtendedAppointment } from '../types';

type State = {
  insuranceStatusCalendarDate?: string;
  selectedAppointments: ExtendedAppointment[];
  selectedAppointment?: ExtendedAppointment;
  selectedAppointmentId?: string;
};

type Actions = {
  setSelectedAppointments: (appointments: ExtendedAppointment[], insuranceStatusCalendarDate: string) => void;
  setSelectedAppointment: (appointment: ExtendedAppointment, reset?: boolean) => void;
  setSelectedAppointmentId: (appointmentId: string) => void;
  resetSelectedAppointment: () => void;
  resetSelection: () => void;
};

const initialState: State = {
  insuranceStatusCalendarDate: undefined,
  selectedAppointments: [],
};

type ScheduleAppointmentInfoPanelStore = State & Actions;

const useStore = createStore<ScheduleAppointmentInfoPanelStore>(
  (set) => ({
    ...initialState,
    setSelectedAppointments: (appointments, insuranceStatusCalendarDate) => {
      set({
        insuranceStatusCalendarDate,
        selectedAppointments: appointments,
        // auto select appointment if only one is selected
        selectedAppointment: appointments.length === 1 ? appointments[0] : undefined,
        selectedAppointmentId: appointments.length === 1 ? appointments[0].id : undefined,
      });
    },
    setSelectedAppointment: (appointment, reset = false) => {
      if (reset) {
        set({
          selectedAppointment: appointment,
          selectedAppointmentId: appointment.id,
          selectedAppointments: [],
          insuranceStatusCalendarDate: undefined,
        });
      } else {
        set({ selectedAppointment: appointment, selectedAppointmentId: appointment.id });
      }
    },
    setSelectedAppointmentId: (appointmentId) => {
      set({
        selectedAppointmentId: appointmentId,
        selectedAppointment: undefined,
        selectedAppointments: [],
        insuranceStatusCalendarDate: undefined,
      });
    },
    resetSelectedAppointment: () => set({ selectedAppointment: undefined, selectedAppointmentId: undefined }),
    resetSelection: () =>
      set({
        selectedAppointments: [],
        selectedAppointment: undefined,
        selectedAppointmentId: undefined,
        insuranceStatusCalendarDate: undefined,
      }),
  }),
  {
    name: 'ScheduleAppointmentInfoPanelStore',
    enabled: true,
    trace: true,
  }
);

export const useScheduleAppointmentInfoPanelShallowStore = createShallowStore(useStore);

export const useSetCalendarAppointmentInfoAsSelected = (
  eventId: string,
  locationId: string,
  cachedAppointments?: ExtendedAppointment[]
) => {
  const {
    selectedAppointment,
    selectedAppointmentId,
    setSelectedAppointment,
    setSelectedAppointmentId,
    resetSelection,
  } = useScheduleAppointmentInfoPanelShallowStore(
    'selectedAppointment',
    'selectedAppointmentId',
    'setSelectedAppointment',
    'setSelectedAppointmentId',
    'resetSelection'
  );
  // get appointment list based on calendar context & cached queries
  const apptInfoFromCalendar = useGetSelectedCalendarDateAppointmentInfo(eventId);

  // Memoize apptInfoFromCache to prevent creating new object references on every render
  const apptInfoFromCache = useMemo(() => {
    if (apptInfoFromCalendar) {
      return apptInfoFromCalendar;
    }
    return cachedAppointments?.find((appt) => appt.id === eventId);
    // SP-TODO: Find better way to handle deps here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(apptInfoFromCalendar), JSON.stringify(cachedAppointments), eventId]);

  const isSelected = selectedAppointmentId === eventId;

  // need to sync selected appointment info if selected to keep data consistent on updates outside of this store
  useEffect(() => {
    if (isSelected && apptInfoFromCache) {
      setSelectedAppointment(apptInfoFromCache);
    }
  }, [isSelected, apptInfoFromCache, setSelectedAppointment]);

  return {
    isSelected,
    selectedAppointment,
    setAppointment: (reset?: boolean) => {
      if (apptInfoFromCache) {
        setSelectedAppointment(apptInfoFromCache, reset);
      } else {
        console.error('[useSetCalendarAppointmentInfoAsSelected] appointment info not found.');
        sentry.error({
          topic: 'schedule-calendar',
          team: 'schedule',
          error: `Appointment info not found.`,
          addContext: { name: 'details', context: { eventId, locationId } },
        });
        setSelectedAppointmentId(eventId);
      }
    },
    resetSelection,
  };
};

export const useSetCalendarAppointmentListAsSelected = (eventIds: string[], calendarDate: string) => {
  const { setSelectedAppointments, selectedAppointments } = useScheduleAppointmentInfoPanelShallowStore(
    'setSelectedAppointments',
    'selectedAppointments'
  );
  // get appointment list based on calendar context & cached queries
  const appointments = useGetSelectedCalendarDateAppointmentList(eventIds);

  const selectedAppointmentIds = useMemo(() => selectedAppointments.map((appt) => appt.id), [selectedAppointments]);

  const hasSelectedAppointmentsInList = useMemo(
    () => eventIds.some((eventId) => selectedAppointmentIds.includes(eventId)),
    [eventIds, selectedAppointmentIds]
  );

  const shouldSyncSelection = selectedAppointmentIds?.length !== eventIds?.length;

  // on resize of calendar view, sync current overlapping events
  useEffect(() => {
    if (hasSelectedAppointmentsInList && shouldSyncSelection) {
      setSelectedAppointments(appointments, calendarDate);
    }
  }, [appointments, hasSelectedAppointmentsInList, shouldSyncSelection, setSelectedAppointments, calendarDate]);

  return {
    isSelected: hasSelectedAppointmentsInList,
    selectedAppointments,
    setAppointments: () => setSelectedAppointments(appointments, calendarDate),
  };
};
